import { Editor } from '@tiptap/core';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import LinkEditorModal from './LinkEditorModal';
import { applyLink } from './utils';

type LinkControllerProps = {
  editor: Editor | null;
  opened: boolean;
  onClose: () => void;
};

const LinkControllerComponent: React.FC<LinkControllerProps> = ({ editor, opened, onClose }) => {
  const [linkValue, setLinkValue] = React.useState('');
  const [isEditingLink, setIsEditingLink] = React.useState(false);

  const handleApplyLink = React.useCallback(
    (href: string | null) => {
      if (!editor) {
        return;
      }

      applyLink(editor, href);
      onClose();
      setLinkValue('');
      setIsEditingLink(false);
    },
    [editor, onClose],
  );

  const handleSubmit = React.useCallback(() => {
    const value = linkValue.trim();

    handleApplyLink(value === '' ? null : value);
  }, [handleApplyLink, linkValue]);

  const handleRemove = React.useCallback(() => {
    handleApplyLink(null);
  }, [handleApplyLink]);

  const handleChange = React.useCallback((value: string) => {
    setLinkValue(value);
  }, []);

  React.useEffect(() => {
    if (!opened) {
      setLinkValue('');
      setIsEditingLink(false);

      return;
    }

    if (!editor) {
      onClose();

      return;
    }

    setIsEditingLink(editor.isActive('link'));
    const currentHref = editor.getAttributes('link')?.href;

    setLinkValue(typeof currentHref === 'string' ? currentHref : '');
  }, [editor, onClose, opened]);

  return (
    <LinkEditorModal
      opened={opened}
      value={linkValue}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onRemove={handleRemove}
      onClose={onClose}
      isEditing={isEditingLink}
    />
  );
};

LinkControllerComponent.displayName = 'LinkController';

export default observer(LinkControllerComponent);
